import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/formatDate';
import { 
  FileJson, RefreshCw, Eye, Download, Trash2, Upload,
  ChevronLeft, ChevronRight, Zap, ExternalLink,
  CheckCircle, AlertCircle, Clock, X, File
} from 'lucide-react';
import { apiCall, v3Extract } from '../api';
import '../components/V1WorkOrdersList.css';

// Model config for V3
const MODEL_CONFIG = {
  name: "XOrbit V3",
  description: "Preprocessing + Bedrock Qwen VL",
  color: "#10b981",
  bgColor: "#d1fae5",
};

// Custom Dropdown Component for Status Filter
const CustomStatusDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = [
    { value: "all", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
  ];

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="v1-custom-dropdown" ref={dropdownRef}>
      <button className="v1-dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span>{selectedOption?.label}</span>
        <svg className={`dropdown-arrow ${isOpen ? "open" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      {isOpen && (
        <div className="v1-dropdown-menu">
          {options.map((option) => (
            <button
              key={option.value}
              className={`v1-dropdown-option ${value === option.value ? "selected" : ""}`}
              onClick={() => { onChange(option.value); setIsOpen(false); }}
            >
              {option.label}
              {value === option.value && <span className="check-mark">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadResults, setUploadResults] = useState([]);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const pdfFiles = newFiles.filter(f => 
      f.name.toLowerCase().endsWith('.pdf') || 
      f.name.toLowerCase().endsWith('.tiff') || 
      f.name.toLowerCase().endsWith('.tif') ||
      f.name.toLowerCase().endsWith('.jpg') ||
      f.name.toLowerCase().endsWith('.jpeg') ||
      f.name.toLowerCase().endsWith('.png')
    );
    const existingNames = files.map(f => f.name);
    const uniqueFiles = pdfFiles.filter(f => !existingNames.includes(f.name));
    
    if (pdfFiles.length !== newFiles.length) {
      alert('Only PDF, TIFF, and image files are supported.');
    }
    
    setFiles(prev => [...prev, ...uniqueFiles]);
    
    const newStatus = {};
    uniqueFiles.forEach(f => {
      newStatus[f.name] = 'pending';
    });
    setUploadStatus(prev => ({ ...prev, ...newStatus }));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
    
    const droppedFiles = Array.from(event.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.add('drag-over');
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
  };

  const removeFile = (filename) => {
    setFiles(prev => prev.filter(f => f.name !== filename));
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[filename];
      return newStatus;
    });
  };

  const handleUploadAll = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    const results = [];

    for (const file of files) {
      setUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }));
      
      try {
        const result = await v3Extract(file);
        setUploadStatus(prev => ({ ...prev, [file.name]: 'success' }));
        results.push({ filename: file.name, success: true, work_order_id: result.work_order_id });
      } catch (err) {
        setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
        results.push({ filename: file.name, success: false, error: err.message });
      }
    }

    setUploadResults(results);
    setUploading(false);
    
    if (onUploadComplete) {
      onUploadComplete(results);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setUploadStatus({});
    setUploadResults([]);
    onClose();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return <RefreshCw size={16} className="spin" style={{ color: '#3b82f6' }} />;
      case 'success':
        return <CheckCircle size={16} style={{ color: '#22c55e' }} />;
      case 'error':
        return <AlertCircle size={16} style={{ color: '#ef4444' }} />;
      default:
        return <Clock size={16} style={{ color: '#9ca3af' }} />;
    }
  };

  if (!isOpen) return null;

  const allUploaded = files.length > 0 && files.every(f => uploadStatus[f.name] === 'success' || uploadStatus[f.name] === 'error');
  const successCount = Object.values(uploadStatus).filter(s => s === 'success').length;
  const errorCount = Object.values(uploadStatus).filter(s => s === 'error').length;

  return (
    <div className="v1-modal-overlay" onClick={handleClose}>
      <div className="v1-upload-modal" onClick={e => e.stopPropagation()}>
        <div className="v1-modal-header" style={{ borderBottomColor: MODEL_CONFIG.color }}>
          <h3>
            <Upload size={20} style={{ color: MODEL_CONFIG.color }} />
            Upload Files - {MODEL_CONFIG.name}
          </h3>
          <button className="v1-modal-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <div className="v1-modal-body">
          <div
            ref={dropZoneRef}
            className="v1-drop-zone"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={48} style={{ color: MODEL_CONFIG.color }} />
            <p>Drag & drop files here</p>
            <span>PDF, TIFF, JPG, PNG supported</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.tiff,.tif,.jpg,.jpeg,.png"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {files.length > 0 && (
            <div className="v1-file-list">
              <div className="v1-file-list-header">
                <span>{files.length} file(s) selected</span>
                {allUploaded && (
                  <span className="v1-upload-summary">
                    {successCount} success, {errorCount} failed
                  </span>
                )}
              </div>
              <div className="v1-file-items">
                {files.map(file => (
                  <div key={file.name} className={`v1-file-item ${uploadStatus[file.name]}`}>
                    <div className="v1-file-info">
                      <File size={16} />
                      <span className="v1-file-name" title={file.name}>
                        {file.name.length > 40 ? file.name.substring(0, 40) + '...' : file.name}
                      </span>
                      <span className="v1-file-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    {uploadStatus[file.name] === 'error' && uploadResults.find(r => r.filename === file.name)?.error && (
                      <div className="v1-file-error" style={{ color: '#ef4444', fontSize: 12, padding: '4px 0 0 24px', wordBreak: 'break-word' }}>
                        {uploadResults.find(r => r.filename === file.name)?.error}
                      </div>
                    )}
                    <div className="v1-file-actions">
                      {getStatusIcon(uploadStatus[file.name])}
                      {uploadStatus[file.name] === 'pending' && !uploading && (
                        <button 
                          className="v1-file-remove"
                          onClick={() => removeFile(file.name)}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="v1-modal-footer">
          <button className="v1-btn-cancel" onClick={handleClose}>
            {allUploaded ? 'Close' : 'Cancel'}
          </button>
          {!allUploaded && (
            <button
              className="v1-btn-upload-all"
              style={{ backgroundColor: MODEL_CONFIG.color }}
              onClick={handleUploadAll}
              disabled={files.length === 0 || uploading}
            >
              {uploading ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  Extracting...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Upload & Extract {files.length > 0 ? `(${files.length})` : ''}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const V3WorkOrders = () => {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0, page: 1, pages: 1, limit: 10, skip: 0,
  });
  const [stats, setStats] = useState({
    total: 0, completed: 0, processing: 0, pending: 0, failed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);

  const fetchData = useCallback(async (skip = 0) => {
    try {
      setLoading(true);
      setError("");
      
      let url = `/v1/v3/work-orders?skip=${skip}&limit=10`;
      if (statusFilter !== "all") {
        url += `&status=${statusFilter}`;
      }
      
      const data = await apiCall(url);
      
      if (data.data && data.pagination) {
        setWorkOrders(data.data);
        setPagination({
          total: data.pagination.total,
          page: data.pagination.page,
          pages: data.pagination.pages,
          limit: data.pagination.limit,
          skip: skip
        });
        
        // Calculate stats from current data (simple count)
        const completed = data.data.filter(wo => wo.status === 'completed').length;
        const processing = data.data.filter(wo => wo.status === 'processing').length;
        const pending = data.data.filter(wo => wo.status === 'pending' || wo.status === 'created').length;
        const failed = data.data.filter(wo => wo.status === 'failed').length;
        
        setStats({
          total: data.pagination.total,
          completed: completed,
          processing: processing,
          pending: pending,
          failed: failed,
        });
      } else {
        setWorkOrders(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching V3 work orders:', err);
      setError('Failed to load V3 work orders');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData(0);
  }, [fetchData, statusFilter]);

  const handleDelete = async (workOrderId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this work order?")) return;

    try {
      await apiCall(`/v1/work-orders/${workOrderId}`, { method: 'DELETE' });
      fetchData(pagination.skip);
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) {
      fetchData(pagination.skip + pagination.limit);
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      fetchData(Math.max(0, pagination.skip - pagination.limit));
    }
  };

  const handleOpenV3Docs = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user._id || user.id || '';
    const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:5001'
      : `${window.location.protocol}//${window.location.hostname}`;
    const v3DocsUrl = `${baseUrl}/api/extraction/xorbit/v3/docs?uid=${encodeURIComponent(userId)}`;
    window.open(v3DocsUrl, '_blank');
  };

  const filteredWorkOrders = workOrders.filter((wo) => {
    const term = searchTerm.toLowerCase();
    return (
      wo._id?.toLowerCase().includes(term) ||
      wo.filename?.toLowerCase().includes(term) ||
      wo.uid?.toLowerCase().includes(term) ||
      wo.trace_id?.toLowerCase().includes(term)
    );
  });

  const getStatusBadgeClass = (status) => {
    const classes = {
      completed: "status-completed",
      processing: "status-processing",
      pending: "status-pending",
      created: "status-pending",
      failed: "status-failed",
    };
    return classes[status] || "status-pending";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle size={12} />;
      case 'processing': return <RefreshCw size={12} className="spin" />;
      case 'failed': return <AlertCircle size={12} />;
      default: return <Clock size={12} />;
    }
  };

  return (
    <div className="v1-work-orders-list">
      {/* Header with Model Info */}
      <div className="v1-header-banner" style={{ borderLeftColor: MODEL_CONFIG.color }}>
        <div className="v1-header-info">
          <h2 style={{ color: MODEL_CONFIG.color }}>
            <Zap size={24} />
            {MODEL_CONFIG.name} Work Orders
          </h2>
          <p>{MODEL_CONFIG.description} - V3 Extraction Pipeline</p>
        </div>
        <div className="v1-header-actions">
          <button
            className="v1-btn-upload"
            onClick={() => setShowUploadModal(true)}
            style={{ backgroundColor: MODEL_CONFIG.color }}
          >
            <Upload size={18} /> Upload & Extract
          </button>
          <button
            className="v1-btn-secondary"
            onClick={handleOpenV3Docs}
            style={{ borderColor: MODEL_CONFIG.color, color: MODEL_CONFIG.color }}
          >
            <ExternalLink size={18} /> API Docs
          </button>
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={(results) => {
          setShowUploadModal(false);
          fetchData(0);
        }}
      />

      {error && (
        <div className="v1-error-banner">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="v1-stats-grid">
        <div className="v1-stat-card">
          <div className="v1-stat-value">{stats.total}</div>
          <div className="v1-stat-label">Total</div>
        </div>
        <div className="v1-stat-card completed">
          <div className="v1-stat-value">{stats.completed}</div>
          <div className="v1-stat-label">Completed</div>
        </div>
        <div className="v1-stat-card processing">
          <div className="v1-stat-value">{stats.processing}</div>
          <div className="v1-stat-label">Processing</div>
        </div>
        <div className="v1-stat-card pending">
          <div className="v1-stat-value">{stats.pending}</div>
          <div className="v1-stat-label">Pending</div>
        </div>
        <div className="v1-stat-card failed">
          <div className="v1-stat-value">{stats.failed}</div>
          <div className="v1-stat-label">Failed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="v1-filters-row">
        <input
          type="text"
          placeholder="Search by ID, filename, or UID..."
          className="v1-search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ '--focus-color': MODEL_CONFIG.color }}
        />
        <CustomStatusDropdown value={statusFilter} onChange={setStatusFilter} />
        <button className="v1-btn-refresh" onClick={() => fetchData(0)}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="v1-table-container">
        <table className="v1-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>UID</th>
              <th>Filename</th>
              <th>Model</th>
              <th>Status</th>
              <th>Pages</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="v1-table-loading">
                  <RefreshCw size={20} className="spin" /> Loading V3 work orders...
                </td>
              </tr>
            ) : filteredWorkOrders.length === 0 ? (
              <tr>
                <td colSpan="8" className="v1-table-empty">
                  No V3 work orders found. Use the API to create extractions.
                </td>
              </tr>
            ) : (
              filteredWorkOrders.map((wo) => (
                <tr key={wo._id} onClick={() => navigate(`/v1/v3/work-order/${wo._id}`)}>
                  <td className="v1-id-cell">{wo._id?.slice(-8)}</td>
                  <td>
                    <span style={{ 
                      color: MODEL_CONFIG.color, 
                      fontWeight: 500,
                      fontSize: '0.85rem'
                    }}>
                      {wo.uid || wo.trace_id || '-'}
                    </span>
                  </td>
                  <td className="v1-filename-cell" title={wo.filename}>
                    {wo.filename?.substring(0, 30) || wo.name || "-"}
                    {wo.filename?.length > 30 && "..."}
                  </td>
                  <td>
                    <span
                      className="v1-model-badge"
                      style={{
                        backgroundColor: MODEL_CONFIG.bgColor,
                        color: MODEL_CONFIG.color,
                      }}
                    >
                      Qwen VL
                    </span>
                  </td>
                  <td>
                    <span className={`v1-status-badge ${getStatusBadgeClass(wo.status)}`}>
                      {getStatusIcon(wo.status)}
                      <span style={{ marginLeft: '4px' }}>{wo.status}</span>
                    </span>
                  </td>
                  <td>{wo.total_pages || wo.totalPages || "-"}</td>
                  <td>{formatDate(wo.created_at || wo.createdAt)}</td>
                  <td className="v1-actions-cell" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="v1-action-btn view"
                      title="View Details"
                      onClick={() => navigate(`/v1/v3/work-order/${wo._id}`)}
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="v1-action-btn json"
                      title="Download JSON"
                      style={{ color: MODEL_CONFIG.color }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const data = wo.extracted_data || wo.extractedData || {};
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${wo.filename?.replace(".pdf", "") || wo._id}.json`;
                        a.click();
                      }}
                    >
                      <FileJson size={16} />
                    </button>
                    {wo.status === 'completed' && (
                      <a
                        href={`/v1/work-orders/${wo._id}/pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="v1-action-btn"
                        title="Download PDF"
                        style={{ color: '#64748b' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Download size={16} />
                      </a>
                    )}
                    <button
                      className="v1-action-btn delete"
                      title="Delete"
                      onClick={(e) => handleDelete(wo._id, e)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="v1-pagination">
        <div className="v1-pagination-info">
          Showing {workOrders.length > 0 ? pagination.skip + 1 : 0} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
        </div>
        <div className="v1-pagination-controls">
          <button onClick={handlePrevPage} disabled={pagination.page <= 1}>
            <ChevronLeft size={16} /> Previous
          </button>
          <span className="v1-page-indicator">
            Page {pagination.page} of {pagination.pages || 1}
          </span>
          <button onClick={handleNextPage} disabled={pagination.page >= pagination.pages}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default V3WorkOrders;
